/* 12-pattern internal classifier + the 7-bucket display taxonomy.
   Extracted verbatim from artifact.html <script id="logic">.

   INVARIANT: two layers. classifyQuery() returns the INTERNAL pattern (12 values);
   DISPLAY_BUCKET maps it to one of the 7 user-facing buckets. The internal pattern
   must be retained as `detected_pattern` in the drawer and in both CSV exports.
   Do not collapse the layers. */

const ARTEFACT_WORDS = new Set("yes no ok okay sure thanks thank you go on continue more tell me got it great perfect nice cool sounds good please yep yeah nah stop wait next again done that one options show".split(" "));

export function isArtefact(s, words) {
  if (words > 5) return false;
  if (/^(yes|ok(ay)?|sure|thanks|thank you|no thanks|sounds good|go on|yes go on|continue|tell me more|got it( continue)?|more( options)?( please)?|show me more|yes that one|next|try again|do it|why not|and then|what else|stop|wait)$/.test(s)) return true;
  return s.split(" ").every(w => ARTEFACT_WORDS.has(w));
}

export function classifyQuery(qRaw) {
  const q = String(qRaw);
  const s = q.toLowerCase().replace(/\s+/g, " ").trim();
  const words = s.split(" ").length;
  const done = (bucket, conf, hits, tier, cscore) => ({ bucket, tier: tier || null, score: 0, cscore: cscore != null ? cscore : 0, conf, hits: hits || [], by: "rule" });

  if (isArtefact(s, words)) return done("artefact", 97, ["Conversation acknowledgement fragment", "No topical subject"], null, 96);
  if (words <= 7 && /^(what about|how about|and (what about|how about|for)|what if|same (for|but))\b/.test(s))
    return done("pivot", 95, ["Topic-shift opener", "Depends on prior context"], null, 90);
  if (/(ignore (all )?previous|you are (a|an) [a-z]+ (expert|assistant|doctor)|act as (a|an) |system prompt|respond (only )?in json|as an ai (model|assistant))/.test(s))
    return done("agent_harness", 96, ["Agent instruction pattern"], null, 40);
  if (/\?\. my location is |\. my location is /.test(s))
    return done("tracker_probe", 97, ["Rank-tracker template suffix"], null, 5);
  if (/\* ?[a-d][).]|(\b[a-d]\) [^)]{2,40}){2,}|\bwhich of the following\b|[￼] ?[a-d][.)]/.test(s))
    return done("quiz_exam", 94, ["Multiple-choice / exam question pattern"], null, 10);
  if (/https?:\/\/|www\.|\.(com|in|org|io|net|co)\b/.test(s))
    return done("navigational", 95, ["URL or domain string"], null, 3);
  if (/\b(login|log in|sign ?in|sign ?up|download app|customer care|order tracking|coupon code|franchise|store locator|contact number)\b/.test(s) && words <= 6)
    return done("navigational", 93, ["Site-access intent phrase"], null, 3);
  if (words >= 4 && /\breviews?$/.test(s) && /\b(clinic|centre|center|hospital|agency|company|studio|salon|spa|store|restaurant|hotel)\b/.test(s))
    return done("navigational", 90, ["Business-listing lookup"], null, 5);
  const longestTok = s.replace(/[^a-z]/g, " ").split(" ").reduce((m, t) => Math.max(m, t.length), 0);
  if (words >= 4 && longestTok >= 18)
    return done("pasted_string", 90, ["Concatenated text run without separators"], null, 15);
  if (/\|/.test(s)) return done("long_uncategorised", 92, ["Pipe-separated listing string"], null, 5);

  let score = 0; const hits = [];
  const add = (n, label) => { score += n; if (n > 0 && label) hits.push(label); };
  if (/(kya (karen|kare\b|karna|khana|khaen)|karna chahie|karna chahiye|khana chahie|khana chahiye|क्या करें|क्या करना|क्या खाना|करना चाहिए|खाना चाहिए|क्या करते)/.test(s)) add(2, "Advice-seeking phrasing");
  if (/^(can|could|should|would|shall) (i|we|you)\b/.test(s)) add(2, "Direct personal question");
  if (/^(i|we)('m| am| want| need| have| run| tried| would like)\b/.test(s) || /^(give|tell|show|write|make|explain|help|suggest) me\b/.test(s) || /^explain like\b/.test(s) || /^write (me |us )?a\b/.test(s) || /^suggest\b/.test(s)) add(2, "First-person request or instruction");
  if (/\?\s*$/.test(q)) add(2, "Terminal question mark");
  if (/\b(recommend)\b/.test(s)) add(2, "Recommendation request");
  if (/^(do|does|did|is|are|was|were) (you|i|we)\b/.test(s) || /^is it safe\b/.test(s)) add(2, "Yes/No question form");
  if (/how (do|can|should) (i|we|you)\b|how to know if\b/.test(s)) add(2, "Personal how-question");
  if (/(ho to kya|ho raha hai (to )?kya|badh (jaye|jaaye) to|हो तो क्या|हो रहा है|mujhe .* (hai|hain))/.test(s)) add(2, "Conditional / personal problem statement");
  if (/(prakash daliye|samjhaie|samjhaiye|batao|bataiye|बताइए|समझाइए)/.test(s)) add(2, "Imperative explanation request");
  if (/\b(my (site|traffic|store|blog|website|rankings?|hair|skin|knee|back|child|kid)|our (site|traffic))\b/.test(s)) add(2, "Personal-situation reference");
  if (/(please keep it simple|in simple (words|terms)|tell me in simple|step by step|nothing works|please suggest)/.test(s)) add(1, "Style / plea directed at responder");
  if (/\b(i am \d+|years? old|my \d+ year old)\b/.test(s)) add(1, "Personal constraint stated");

  if (/^(how|what|why|which|where|when|who)\b/.test(s) && words >= 5) add(1, "Question-word opening");
  if (/(kaise|kis (roop|kam|prakar)|kitne|kaun|कैसे|किस|कितने|कौन)/.test(s)) add(1, "Hindi interrogative");
  if (/( (aata|aati|hota|hote|hoti) (hai|hain)\b)/.test(s)) add(1, "Question-verb structure");
  if (words >= 7) add(1, "Natural-language length");
  if (/\b(i|my|me|mujhe|mera|meri|hamare|मुझे|मेरा|मेरी)\b/.test(s)) add(1, "First-person reference");
  if (/(paribhashit|परिभाषित| se kya labh| ka matlab| meaning in)/.test(s)) add(1, "Definition-seeking phrasing");

  if (/^(best|top) /.test(s) || /\bbest [a-z ]{0,40}\b(for|in|without)\b/.test(s)) add(-3, "");
  if (/^(is|are) there (any|an)\b/.test(s)) add(-1, "");
  if (words <= 3) add(-2, "");
  if (/(–|—|\(\)| reviews?$| price$| online$)/.test(s)) add(-1, "");

  let bucket, tier = null;
  const isQuestion = (/^(how|what|why|which|where|when|who|do|does|did|is|are|was|were)\b/.test(s) ||
    /(kaise|kya |kitne|kaun|कैसे|क्या|कितने|कौन)/.test(s) || /\?\s*$/.test(q)) &&
    !/^(best|top) /.test(s) && !/\bbest\b/.test(s);

  if (words >= 16 && score < 2) bucket = "long_uncategorised";
  else if (score >= 5) { bucket = "conversational"; tier = "Strong"; }
  else if (score >= 3) { bucket = "conversational"; tier = "Moderate"; }
  else if (isQuestion && words >= 4) { bucket = "question"; if (!hits.length) hits.push("Interrogative structure, no personal framing"); }
  else if (score === 2 && words >= 5) { bucket = "uncertain"; hits.push("Boundary signal score — could be either"); }
  else bucket = "ordinary";
  const cscore = Math.max(0, Math.min(99, score * 9 + 5));
  const conf = bucket === "conversational" ? Math.min(97, 48 + score * 8)
    : bucket === "question" ? Math.min(97, 70 + score * 5)
    : bucket === "uncertain" ? 55
    : Math.min(97, 58 + Math.max(0, 3 - score) * 12);
  return { bucket, tier, score, cscore, conf, hits, by: "rule" };
}

/* 7-bucket display taxonomy over the internal 12-pattern detection. */
export const DISPLAY_BUCKET = {
  ordinary: "ordinary", navigational: "ordinary",
  conversational: "conversational", question: "conversational", artefact: "conversational",
  pivot: "pivot",
  tracker_probe: "tracker_probe",
  pasted_string: "pasted_string", agent_harness: "pasted_string", quiz_exam: "pasted_string",
  long_uncategorised: "long_uncategorised",
  uncertain: "uncertain",
};

/* Display order of the 7 buckets (drives filter dropdowns and count pills). */
export const BUCKET_ORDER = ["conversational", "ordinary", "pivot", "uncertain", "tracker_probe", "pasted_string", "long_uncategorised"];
